"use client";

import type { Visit } from "@gettactic/api";
import { StatusBadge } from "@gettactic/components/src/data-display/StatusBadge";
import { VisitorLabel } from "@gettactic/components/src/kiosk/VisitorLabel";
import { useDisclosure } from "@gettactic/helpers";
import { useAuthenticated } from "@gettactic/hooks";
import { format, parseISO } from "date-fns";
import { CalendarCheck, Printer, UserCheck, Users } from "lucide-react";
import React, { useMemo } from "react";
import { DashboardGreeting } from "@/features/dashboard/components/DashboardGreeting";
import { StatCard } from "@/features/dashboard/components/StatCard";
import { useDashboardFilters } from "@/features/dashboard/DashboardStateContext";
import { InvitationLinkModal } from "@/features/visitors/components/InvitationLinkModal";
import { InviteDropdownButton } from "@/features/visitors/components/InviteDropdownButton";
import { PreRegisterVisitorModal } from "@/features/visitors/components/PreRegisterVisitorModal";
import { useVisitorBadgePrinter } from "@/features/visitors/hooks/useVisitorBadgePrinter";
import {
  useVisitorsContext,
  VisitorsProvider
} from "@/features/visitors/views/VisitorsContext";

const getStatusColor = (
  status: Visit["status"]
): "green" | "blue" | "neutral" => {
  switch (status) {
    case "checked_in":
      return "green";
    case "expected":
      return "blue";
    case "checked_out":
      return "neutral";
    default:
      return "neutral";
  }
};

const getStatusLabel = (status: Visit["status"]): string => {
  switch (status) {
    case "checked_in":
      return "Checked In";
    case "checked_out":
      return "Checked Out";
    case "expected":
      return "Expected";
    default:
      return status;
  }
};

const VisitorsTableContent: React.FC<{
  onInviteSingleGuest: () => void;
  onCopyRegistrationLink: () => void;
}> = ({ onInviteSingleGuest, onCopyRegistrationLink }) => {
  const { visitors, isLoading } = useVisitorsContext();

  const stats = useMemo(() => {
    const total = visitors.length;
    const preRegistered = visitors.filter(
      (v) => v.status === "expected"
    ).length;
    const checkedIn = visitors.filter((v) => v.status === "checked_in").length;
    return { total, preRegistered, checkedIn };
  }, [visitors]);

  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();

  return (
    <div className="flex flex-col gap-6">
      {/* Greeting */}
      <DashboardGreeting
        name={authenticatedUser.user?.name || ""}
        subtitle={
          <>
            You have{" "}
            <span className="font-medium text-neutral-700">{stats.total}</span>{" "}
            visitor{stats.total !== 1 ? "s" : ""} scheduled to visit this week.
          </>
        }
      />

      {/* Stat Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard
          label="Guests this week"
          value={stats.total}
          icon={<Users className="h-5 w-5" />}
        />
        <StatCard
          label="Pre-registered"
          value={stats.preRegistered}
          icon={<CalendarCheck className="h-5 w-5" />}
        />
        <StatCard
          label="Checked-in"
          value={stats.checkedIn}
          icon={<UserCheck className="h-5 w-5" />}
        />
      </div>

      {/* Visitors this week table */}
      <div className="rounded-xl border border-neutral-200 bg-white">
        <div className="flex items-center justify-between border-neutral-200 border-b px-4 py-3 md:px-6">
          <h2 className="font-semibold text-neutral-900">Visitors this week</h2>
          <InviteDropdownButton
            onInviteSingleGuest={onInviteSingleGuest}
            onCopyRegistrationLink={onCopyRegistrationLink}
          />
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12 text-neutral-400">
            Loading visitors...
          </div>
        ) : visitors.length === 0 ? (
          <div className="flex items-center justify-center py-12 text-neutral-400">
            No visitors scheduled this week
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-neutral-200 border-b bg-neutral-50 text-neutral-500 text-xs uppercase tracking-wider">
                  <th className="px-4 py-3 font-medium md:px-6">Visitor</th>
                  <th className="px-4 py-3 font-medium md:px-6">
                    Date &amp; Time
                  </th>
                  <th className="px-4 py-3 font-medium md:px-6">Status</th>
                  <th className="px-4 py-3 font-medium md:px-6">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {visitors.map((visit) => (
                  <VisitorRow key={visit.id} visit={visit} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const VisitorRow: React.FC<{ visit: Visit }> = ({ visit }) => {
  const printBadge = useVisitorBadgePrinter();
  return (
    <tr className="hover:bg-neutral-50">
      <td className="px-4 py-3 md:px-6">
        <VisitorLabel
          name={visit.visitor?.name || "Unknown Visitor"}
          subtitle={visit.host ? visit.host.name : undefined}
          photoUrl={visit.guest_photo_url}
          size="sm"
        />
      </td>
      <td className="px-4 py-3 text-neutral-600 md:px-6">
        {visit.expected
          ? format(parseISO(visit.expected), "MMM d, yyyy h:mm a")
          : "\u2014"}
      </td>
      <td className="px-4 py-3 md:px-6">
        <StatusBadge
          color={getStatusColor(visit.status)}
          label={getStatusLabel(visit.status)}
          size="sm"
        />
      </td>
      <td className="px-4 py-3 md:px-6">
        <button
          type="button"
          className="rounded p-1 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-600"
          onClick={() => printBadge(visit)}
          title="Print Badge"
        >
          <Printer className="h-4 w-4" />
        </button>
      </td>
    </tr>
  );
};

export const DashboardVisitors: React.FC = () => {
  const { filters } = useDashboardFilters();
  const officeId = filters.offices?.[0] ?? undefined;

  const invitationModal = useDisclosure();
  const preRegisterModal = useDisclosure();

  return (
    <div className="h-full w-full overflow-y-auto pt-4 pb-12 md:pt-16">
      <VisitorsProvider
        activeTab="upcoming"
        initialOfficeId={officeId}
        disableUrlSync
      >
        <VisitorsTableContent
          onInviteSingleGuest={preRegisterModal.open}
          onCopyRegistrationLink={invitationModal.open}
        />
      </VisitorsProvider>

      <InvitationLinkModal
        isOpen={invitationModal.isOpen}
        onOpenChange={invitationModal.onOpenChange}
      />
      <PreRegisterVisitorModal
        isOpen={preRegisterModal.isOpen}
        onOpenChange={preRegisterModal.onOpenChange}
      />
    </div>
  );
};
